import React from 'react';
import {
  cssColorValue,
  desktopFinale,
  desktopFinaleBold,
  desktopMesto,
  desktopMestoBold,
  screenMdMin,
  screenSmMax,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
} from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { FooterSchema, LinkListItem } from 'types/navigation';
import { useTranslation } from 'providers/translations/TranslationProvider';

import { FooterTextLink } from './Link';

const CopyrightPolicies = styled.div`
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: calc(${spacer24} + ${spacer12});

  @media (max-width: ${screenSmMax}) {
    width: 100%;
    justify-content: space-between;
    gap: ${spacer24};
    order: 1;
    align-self: stretch;

    // Languages with long words spill off the screen
    overflow-x: scroll;
    white-space: nowrap;
  }
`;

const CopyrightWrapper = styled.div`
  color: ${cssColorValue(semanticColors.textSubdued)};
  ${() => desktopFinale};
  @media (min-width: ${screenMdMin}) {
    ${() => desktopMesto}
  }
`;

const PoliciesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacer16};
  @media (min-width: ${screenMdMin}) {
    gap: ${spacer32};
  }
`;

const StyledPolicyLink = styled(FooterTextLink)`
  && {
    ${desktopFinaleBold};
    color: ${cssColorValue(semanticColors.textSubdued)};
    @media (min-width: ${screenMdMin}) {
      ${desktopMestoBold}
    }
  }
`;

const copyrightYear = new Date().getFullYear();

interface Props {
  footerEntry: FooterSchema;
}

interface LinkData {
  target: string;
  href: string;
  title: string;
}

export const FooterCopyrightPolicies = ({ footerEntry }: Props) => {
  const { t } = useTranslation();
  const getLinkData = ({ link: { target, href, title } }: LinkListItem): LinkData => ({
    target,
    href,
    title: t(title),
  });

  return (
    <CopyrightPolicies>
      <CopyrightWrapper>&#169; {copyrightYear} Spotify AB</CopyrightWrapper>
      {(footerEntry.corporateLinks || []).map((item, i) => {
        const links = (item.entries || []).map(getLinkData);
        return (
          <PoliciesWrapper key={`${item.label}-${i}`}>
            {links.map(link => (
              <StyledPolicyLink key={link.title} href={link.href} target={link.target}>
                {link.title}
              </StyledPolicyLink>
            ))}
          </PoliciesWrapper>
        );
      })}
    </CopyrightPolicies>
  );
};
